import React, { useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Download, Print, TableChart } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import CaseStudyPrint from './CaseStudyPrint';
import { exportSingleAppointmentToCSV } from '../../../utils/csvExport';

interface CaseStudyPrintDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: any;
  orders: any[];
  hospitalInfo: {
    logoUrl: string;
    name: string;
    address: string;
    phone?: string;
    email?: string;
  };
  consultations?: any[];
  questions?: any[];
}

const CaseStudyPrintDialog: React.FC<CaseStudyPrintDialogProps> = ({ 
  open, 
  onClose, 
  appointment, 
  orders, 
  hospitalInfo, 
  consultations = [], 
  questions = [] 
}) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!printRef.current) return;
    const printContents = printRef.current.innerHTML;
    const win = window.open('', '', 'width=900,height=650');
    if (!win) return;
    win.document.write('<html><head><title>Case Study Report</title>');
    win.document.write('<style>body{margin:0;font-family:sans-serif;}@media print{@page{size:A4;margin:16mm;}}</style>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    
    try {
      // Create canvas from the case study element
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename with appointment number and date
      const appointmentNumber = appointment?.appointmentNumber || appointment?.id || 'CS';
      const patientName = appointment?.patient?.fullName?.replace(/\s+/g, '_') || 'Patient';
      const date = new Date().toISOString().split('T')[0];
      const filename = `CaseStudy_${patientName}_${appointmentNumber}_${date}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const success = exportSingleAppointmentToCSV(
        appointment,
        orders,
        consultations,
        questions
      );
      
      if (success) {
        console.log('CSV export successful');
      } else {
        alert('Error exporting CSV. Please try again.');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting CSV. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Print Case Study Report</DialogTitle>
      <DialogContent>
        <div ref={printRef}>
          <CaseStudyPrint 
            appointment={appointment} 
            orders={orders} 
            hospitalInfo={hospitalInfo}
            consultations={consultations}
            questions={questions}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button 
          onClick={handleExportCSV} 
          variant="outlined" 
          color="secondary" 
          startIcon={<TableChart />}
          sx={{ mr: 1 }}
        >
          Export CSV
        </Button>
        <Button 
          onClick={handleDownloadPDF} 
          variant="outlined" 
          color="primary" 
          startIcon={<Download />}
          sx={{ mr: 1 }}
        >
          Download PDF
        </Button>
        <Button 
          onClick={handlePrint} 
          variant="contained" 
          color="primary" 
          startIcon={<Print />}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseStudyPrintDialog;
